import { classNames } from '@fullstory/util/fmt';
import React, { PropsWithChildren } from 'react';
import cssAlignment from './alignment.module.scss';
import cssColor from './color.module.scss';
import cssFont from './font.module.scss';
import css from './index.module.scss';
import cssUnderline from './underline.module.scss';
import cssWeight from './weight.module.scss';

type CSSProperties =
  | 'fontSize'
  | 'fontStyle'
  | 'lineHeight'
  | 'letterSpacing'
  | 'whiteSpace'
  | 'wordBreak'
  | 'textTransform'
  | 'overflowWrap';

const variantMap = Object.freeze({
  title1: {
    fontFamily: 'serif' as const,
    fontSize: 'var(--typography-t1-font-size)',
    lineHeight: 'var(--typography-t1-line-height)',
    fontWeight: 'var(--typography-t1-font-weight)',
    letterSpacing: -0.5,
    textTransform: undefined,
  },
  title2: {
    fontFamily: 'serif' as const,
    fontSize: 'var(--typography-t2-font-size)',
    lineHeight: 'var(--typography-t2-line-height)',
    fontWeight: 'var(--typography-t2-font-weight)',
    letterSpacing: -0.5,
    textTransform: undefined,
  },
  title3: {
    fontFamily: 'sans' as const,
    fontSize: 'var(--typography-t3-font-size)',
    lineHeight: 'var(--typography-t3-line-height)',
    fontWeight: 'var(--typography-t3-font-weight)',
    letterSpacing: undefined,
    textTransform: undefined,
  },
  heading1: {
    fontFamily: 'sans' as const,
    fontSize: 'var(--typography-h1-font-size)',
    lineHeight: 'var(--typography-h1-line-height)',
    fontWeight: 'var(--typography-h1-font-weight)',
    letterSpacing: undefined,
    textTransform: undefined,
  },
  heading2: {
    fontFamily: 'sans' as const,
    fontSize: 'var(--typography-h2-font-size)',
    lineHeight: 'var(--typography-h2-line-height)',
    fontWeight: 'var(--typography-h2-font-weight)',
    letterSpacing: undefined,
    textTransform: undefined,
  },
  heading3: {
    fontFamily: 'sans' as const,
    fontSize: 'var(--typography-h3-font-size)',
    lineHeight: 'var(--typography-h3-line-height)',
    fontWeight: 'var(--typography-h3-font-weight)',
    letterSpacing: undefined,
    textTransform: undefined,
  },
  heading4: {
    fontFamily: 'sans' as const,
    fontSize: 'var(--typography-h4-font-size)',
    lineHeight: 'var(--typography-h4-line-height)',
    fontWeight: 'var(--typography-h4-font-weight)',
    letterSpacing: undefined,
    textTransform: undefined,
  },
  heading5: {
    fontFamily: 'sans' as const,
    fontSize: 'var(--typography-h5-font-size)',
    lineHeight: 'var(--typography-h5-line-height)',
    fontWeight: 'var(--typography-h5-font-weight)',
    letterSpacing: undefined,
    textTransform: undefined,
  },
  heading6: {
    fontFamily: 'sans' as const,
    fontSize: 'var(--typography-h6-font-size)',
    lineHeight: 'var(--typography-h6-line-height)',
    fontWeight: 'var(--typography-h6-font-weight)',
    letterSpacing: 0.75,
    textTransform: 'uppercase' as const,
  },
  heading7: {
    fontFamily: 'sans' as const,
    fontSize: 'var(--typography-h7-font-size)',
    lineHeight: 'var(--typography-h7-line-height)',
    fontWeight: 'var(--typography-h7-font-weight)',
    letterSpacing: 0.5,
    textTransform: 'uppercase' as const,
  },
  heading8: {
    fontFamily: 'sans' as const,
    fontSize: 'var(--typography-h8-font-size)',
    lineHeight: 'var(--typography-h8-line-height)',
    fontWeight: 'var(--typography-h8-font-weight)',
    letterSpacing: 0.5,
    textTransform: 'uppercase' as const,
  },
  body1: {
    fontFamily: 'sans' as const,
    fontSize: 'var(--typography-body1-font-size)',
    lineHeight: 'var(--typography-body1-line-height)',
    fontWeight: 'var(--typography-body1-font-weight)',
    letterSpacing: undefined,
    textTransform: undefined,
  },
  body2: {
    fontFamily: 'sans' as const,
    fontSize: 'var(--typography-body2-font-size)',
    lineHeight: 'var(--typography-body2-line-height)',
    fontWeight: 'var(--typography-body2-font-weight)',
    letterSpacing: undefined,
    textTransform: undefined,
  },
  body3: {
    fontFamily: 'sans' as const,
    fontSize: 'var(--typography-body3-font-size)',
    lineHeight: 'var(--typography-body3-line-height)',
    fontWeight: 'var(--typography-body3-font-weight)',
    letterSpacing: undefined,
    textTransform: undefined,
  },
  body4: {
    fontFamily: 'sans' as const,
    fontSize: 'var(--typography-body4-font-size)',
    lineHeight: 'var(--typography-body4-line-height)',
    fontWeight: 'var(--typography-body4-font-weight)',
    letterSpacing: undefined,
    textTransform: undefined,
  },
  mono1: {
    fontFamily: 'mono' as const,
    fontSize: 'var(--typography-mono1-font-size)',
    lineHeight: 'var(--typography-mono1-line-height)',
    fontWeight: 'var(--typography-mono1-font-weight)',
    letterSpacing: undefined,
    textTransform: undefined,
  },
  mono2: {
    fontFamily: 'mono' as const,
    fontSize: 'var(--typography-mono2-font-size)',
    lineHeight: 'var(--typography-mono2-line-height)',
    fontWeight: 'var(--typography-mono2-font-weight)',
    letterSpacing: undefined,
    textTransform: undefined,
  },
  mono3: {
    fontFamily: 'mono' as const,
    fontSize: 'var(--typography-mono3-font-size)',
    lineHeight: 'var(--typography-mono3-line-height)',
    fontWeight: 'var(--typography-mono3-font-weight)',
    letterSpacing: undefined,
    textTransform: undefined,
  },
  mono4: {
    fontFamily: 'mono' as const,
    fontSize: 'var(--typography-mono4-font-size)',
    lineHeight: 'var(--typography-mono4-line-height)',
    fontWeight: 'var(--typography-mono4-font-weight)',
    letterSpacing: undefined,
    textTransform: undefined,
  },
  serif1: {
    fontFamily: 'serif' as const,
    fontSize: 'var(--typography-serif1-font-size)',
    lineHeight: 'var(--typography-serif1-line-height)',
    fontWeight: 'var(--typography-serif1-font-weight)',
    letterSpacing: 0.5,
    textTransform: undefined,
  },
  serif2: {
    fontFamily: 'serif' as const,
    fontSize: 'var(--typography-serif2-font-size)',
    lineHeight: 'var(--typography-serif2-line-height)',
    fontWeight: 'var(--typography-serif2-font-weight)',
    letterSpacing: 1,
    textTransform: undefined,
  },
  serif3: {
    fontFamily: 'serif' as const,
    fontSize: 'var(--typography-serif3-font-size)',
    lineHeight: 'var(--typography-serif3-line-height)',
    fontWeight: 'var(--typography-serif3-font-weight)',
    letterSpacing: 1,
    textTransform: undefined,
  },
});

export interface TypographyProps
  extends Partial<Pick<React.CSSProperties, CSSProperties>>,
    Partial<React.AriaAttributes> {
  testId?: string;
  variant: keyof typeof variantMap;
  tag?: keyof JSX.IntrinsicElements;
  fontFamily?: keyof typeof cssFont;
  fontWeight?: keyof typeof cssWeight;
  textAlign?: keyof typeof cssAlignment;
  color?: keyof typeof cssColor;

  // solidOnHover underline is an escape hatch to override the global style
  // for a:hover defined in core.scss
  underline?: keyof typeof cssUnderline;
  ellipsize?: boolean;
  // NOTE: The className prop shouldn't be used for custom styles. It exists as an escape hatch for
  // very specific cases (e.g. fs-block)
  className?: string;
  id?: string;
  /**
   * A string that will be passed on to the element as the native html title attribute.
   * https://www.w3schools.com/tags/att_global_title.asp
   */
  title?: string;
}

/**
 * @deprecated in favor of `import { Text } from '@fullstory/components'` instead
 */
export const Typography: React.FC<PropsWithChildren<TypographyProps>> = ({
  testId = 'typography',
  variant,
  tag: Tag = 'span',
  fontFamily = variantMap[variant].fontFamily,
  fontWeight,
  textAlign,
  color = 'neutrals-n10',
  fontSize = variantMap[variant].fontSize,
  fontStyle,
  lineHeight = variantMap[variant].lineHeight,
  letterSpacing = variantMap[variant].letterSpacing,
  whiteSpace,
  textTransform = variantMap[variant].textTransform,
  ellipsize = false,
  className,
  children,
  underline,
  wordBreak = 'normal',
  overflowWrap = 'normal',
  id,
  title,
  ...rest
}) => (
  <Tag
    data-component="Typography"
    data-testid={testId}
    style={{
      fontSize,
      fontStyle,
      // The `fontWeight` style does not allow the use of CSS custom properties, it only supports
      // weights like `regular`, `bold`, etc. as well as numbers (e.g. `500`). To get around this
      // we are casting the value to `initial`.
      fontWeight: !fontWeight ? (variantMap[variant].fontWeight as 'initial') : undefined,
      lineHeight,
      letterSpacing,
      whiteSpace,
      textTransform,
      wordBreak,
      overflowWrap,
    }}
    className={classNames(
      className,
      css.root,
      cssFont[fontFamily],
      fontWeight ? cssWeight[fontWeight] : null,
      textAlign ? cssAlignment[textAlign] : null,
      underline ? cssUnderline[underline] : null,
      cssColor[color],
      {
        [css.ellipsize]: ellipsize,
      },
    )}
    id={id}
    title={title}
    {...rest}
  >
    {children}
  </Tag>
);
